import { useEffect, useState, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Bell, ShoppingBag, Landmark, Wallet, ShieldCheck, Package, MessageCircle, CheckCircle2, ArrowLeft, Inbox } from "lucide-react";
import { toast } from "sonner";

// Notification type → visual metadata (icon + colors)
const TYPE_META = {
  bank_transfer_proof: { Icon: Landmark, color: "text-[#D98A2C]", bg: "bg-[#FFF7ED]" },
  transfer_confirmed:  { Icon: CheckCircle2, color: "text-green-700", bg: "bg-green-50" },
  payout_released:     { Icon: Wallet, color: "text-[#1A3322]", bg: "bg-[#F5F5F0]" },
  kyc_approved:        { Icon: ShieldCheck, color: "text-green-700", bg: "bg-green-50" },
  kyc_rejected:        { Icon: ShieldCheck, color: "text-[#C85A32]", bg: "bg-[#FEF2F2]" },
  new_booking:         { Icon: Package, color: "text-[#1A3322]", bg: "bg-[#F5F5F0]" },
  new_message:         { Icon: MessageCircle, color: "text-[#1A3322]", bg: "bg-white" },
  new_order:           { Icon: ShoppingBag, color: "text-[#C85A32]", bg: "bg-[#FFF7ED]" },
  default:             { Icon: Bell, color: "text-[#1A3322]", bg: "bg-white" },
};

function timeAgo(iso, lang) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(1, Math.floor((now - then) / 1000));
  const isEn = lang === "en";
  if (s < 60) return isEn ? `${s}s ago` : `il y a ${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return isEn ? `${m}m ago` : `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return isEn ? `${h}h ago` : `il y a ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 30) return isEn ? `${d}d ago` : `il y a ${d} j`;
  const mo = Math.floor(d / 30);
  return isEn ? `${mo}mo ago` : `il y a ${mo} mois`;
}

export default function Notifications() {
  const { user, loading: authLoading } = useAuth();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(data || []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  // React to real-time SSE events dispatched by Header.jsx
  useEffect(() => {
    if (!user) return;
    const handler = (e) => {
      const n = e.detail;
      if (!n) return;
      // Insert at the top only if we don't already have it
      setItems((xs) => (xs.some((x) => x.id === n.id) ? xs : [n, ...xs]));
      toast(n.title, { description: n.body });
    };
    window.addEventListener("sukana:notification", handler);
    return () => window.removeEventListener("sukana:notification", handler);
  }, [user]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/connexion"/>;

  const markOne = async (nid) => {
    try {
      await api.post(`/notifications/${nid}/read`);
      setItems((xs) => xs.map((x) => (x.id === nid ? { ...x, read: true } : x)));
    } catch { toast.error(t("common.error")); }
  };

  const markAll = async () => {
    try {
      const { data } = await api.post("/notifications/read-all");
      setItems((xs) => xs.map((x) => ({ ...x, read: true })));
      toast.success(`${data?.count ?? 0} ${t("notif.marked_read")}`);
    } catch { toast.error(t("common.error")); }
  };

  const unread = items.filter((x) => !x.read).length;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="notifications-page">
      <button onClick={() => nav(-1)} className="text-sm text-[#4B5563] hover:text-[#1A3322] mb-4 inline-flex items-center gap-1" data-testid="notif-back">
        <ArrowLeft className="w-4 h-4"/>{t("notif.back")}
      </button>

      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <span className="label-uppercase text-xs text-[#C85A32]">SUKANACOOP</span>
          <h1 className="heading font-extrabold text-3xl sm:text-4xl mt-2 text-[#111812]" data-testid="notif-title">
            {t("notif.title")}
          </h1>
          <p className="text-sm text-[#4B5563] mt-1">
            {unread > 0
              ? <>{unread} <strong>{t("notif.unread")}</strong> · {items.length} {t("notif.total")}</>
              : t("notif.all_read")}
          </p>
        </div>
        {unread > 0 && (
          <Button onClick={markAll} variant="outline" className="rounded-full text-sm" data-testid="notif-mark-all">
            <CheckCircle2 className="w-4 h-4 mr-1.5"/>{t("notif.mark_all_read")}
          </Button>
        )}
      </div>

      {loading && <p className="text-[#4B5563] text-sm">{t("common.loading")}…</p>}

      {!loading && items.length === 0 && (
        <div className="text-center py-16 bg-white border border-[#1A3322]/10 rounded-2xl" data-testid="notif-empty">
          <Inbox className="w-14 h-14 text-[#1A3322]/30 mx-auto mb-3"/>
          <p className="heading font-bold text-lg text-[#111812]">{t("notif.empty_title")}</p>
          <p className="text-sm text-[#4B5563] mt-1 max-w-md mx-auto px-4">{t("notif.empty_desc")}</p>
        </div>
      )}

      <ul className="space-y-2" data-testid="notif-list">
        {items.map((n) => {
          const meta = TYPE_META[n.type] || TYPE_META.default;
          const Icon = meta.Icon;
          return (
            <li
              key={n.id}
              className={`flex items-start gap-4 p-4 rounded-xl border transition ${n.read ? "border-[#1A3322]/10 bg-white opacity-75" : "border-[#C85A32]/30 bg-white shadow-sm"}`}
              data-testid={`notif-item-${n.id}`}
            >
              <div className={`w-10 h-10 rounded-full ${meta.bg} flex items-center justify-center shrink-0`}>
                <Icon className={`w-5 h-5 ${meta.color}`}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <p className={`heading text-sm sm:text-base text-[#111812] ${n.read ? "font-normal" : "font-bold"}`}>{n.title}</p>
                  <span className="text-[11px] text-[#4B5563] shrink-0">{timeAgo(n.created_at, lang)}</span>
                </div>
                <p className="text-sm text-[#4B5563] mt-0.5 leading-snug">{n.body}</p>
              </div>
              {!n.read && (
                <button
                  onClick={() => markOne(n.id)}
                  className="text-[11px] text-[#1A3322] hover:text-[#C85A32] font-semibold self-start pt-1"
                  data-testid={`notif-read-${n.id}`}
                >
                  {t("notif.mark_read")}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
